import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Building2,
  Wallet,
  Users,
  Gift,
  Bell,
  Shield,
  Key,
  Server,
  Save,
  Globe,
  Phone,
  Mail,
  MessageSquare,
  CreditCard,
  Percent,
  Copy,
  AlertTriangle,
  CheckCircle,
  Database,
  Zap,
  Loader2,
  History,
  Receipt,
  Bot,
  Settings2,
  Plus,
  Trash2,
  Send,
  Gamepad2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CurrencySettings } from "@/components/settings/currency-settings";
import { getAppConfigService, type AppConfig } from "@/domain/app-config";
import { GAME_GROUPS } from "@/domain/return-lose";
import { getCurrentParseConfig } from "@/services/adapters/parse/parse-client";
import { useNavigate } from "react-router-dom";
import { SaveConfirmDialog } from "@/components/ui/confirm-dialog";
import { isFeatureEnabled } from "@/config/features.config";

type SettingCategory =
  | "general" // ข้อมูลเว็บไซต์ (websiteInfo) ✅ API
  | "agent" // Agent เกม (agentInfo) ✅ API
  | "finance" // ฝาก-ถอน-TrueWallet (depositInfo, withdrawInfo, webbankInfo) ✅ API
  | "commission" // คอมมิชชั่น (commission) ✅ API
  | "slip" // ตรวจสอบสลิป (slipInfo) ✅ API
  | "ai" // AI Agent (aiAgentConfig) ✅ API
  | "members" // สมาชิก - Mock (รอสร้าง memberConfig)
  | "promotions" // โปรโมชั่น - Mock (รอสร้าง promotionConfig)
  | "notifications" // การแจ้งเตือน - Mock (รอสร้าง notificationConfig)
  | "security" // ความปลอดภัย - API (เชื่อม securityConfig แล้ว)
  | "system"; // ระบบ - Mock (รอสร้าง systemConfig)

// Category icons (moved outside for cleaner code)
const categoryIcons = {
  general: <Building2 className="h-4 w-4" />,
  agent: <Key className="h-4 w-4" />,
  finance: <Wallet className="h-4 w-4" />,
  commission: <Percent className="h-4 w-4" />,
  slip: <Receipt className="h-4 w-4" />,
  ai: <Bot className="h-4 w-4" />,
  members: <Users className="h-4 w-4" />,
  promotions: <Gift className="h-4 w-4" />,
  notifications: <Bell className="h-4 w-4" />,
  security: <Shield className="h-4 w-4" />,
  system: <Server className="h-4 w-4" />,
};

// Category badges
const categoryBadges: Record<SettingCategory, string> = {
  general: "API",
  agent: "API",
  finance: "API",
  commission: "API",
  slip: "API",
  ai: "API", // ✅ AI Agent Config เชื่อม API แล้ว
  members: "DEV",
  promotions: "DEV",
  notifications: "API", // ✅ Telegram Config เชื่อม API แล้ว
  security: "API",
  system: "API", // ✅ V2 Feature Flags เชื่อม API แล้ว
};

// All category IDs
const allCategoryIds: SettingCategory[] = [
  "general",
  "agent",
  "finance",
  "commission",
  "slip",
  "ai",
  "members",
  "promotions",
  "notifications",
  "security",
  "system",
];

// Filter categories based on feature flags
const enabledCategoryIds = allCategoryIds.filter((id) =>
  isFeatureEnabled(`settings/${id}`)
);

export function SettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] =
    useState<SettingCategory>("general");

  // Build categories with translations
  const categories = enabledCategoryIds.map((id) => ({
    id,
    label: t(`settings.categories.${id}`),
    icon: categoryIcons[id],
    description: t(`settings.categories.${id}Desc`),
    badge: categoryBadges[id],
  }));
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [showConfirmSave, setShowConfirmSave] = useState(false);

  // Load AppConfig on mount
  useEffect(() => {
    loadAppConfig();
  }, []);

  const loadAppConfig = async () => {
    try {
      setIsLoading(true);
      const service = getAppConfigService();
      const config = await service.getAppConfig();
      if (config) {
        setAppConfig(config);
        // Update state from config
        setGeneralSettings({
          siteName: config.websiteInfo?.siteName || "WINNER99",
          siteUrl: config.websiteInfo?.publicUrl || "",
          siteLogo: "/logo.png",
          siteDescription: "เกมส์ออนไลน์",
          contactPhone: "",
          contactEmail: "",
          contactLine: "",
          timezone: "Asia/Bangkok",
          currency: config.currency_mode || "TH",
          language: "th",
        });
        setFinanceSettings({
          minDeposit: config.depositInfo?.deposit_min || 100,
          maxDeposit: config.depositInfo?.deposit_max || 500000,
          minWithdraw: config.withdrawInfo?.withdraw_min || 300,
          maxWithdraw: config.withdrawInfo?.withdraw_max || 200000,
          maxWithdrawPerDay:
            config.withdrawInfo?.withdraw_max_perday_amount || 1000000,
          withdrawFee: 0,
          withdrawFeePercent:
            config.withdrawInfo?.withdraw_after_deposit_percent || 0,
          autoApproveWithdraw: true,
          autoApproveLimit: 10000,
          withdrawProcessTime:
            config.withdrawInfo?.withdraw_after_deposit_time || 5,
          depositBonus: true,
          firstDepositBonus: 100,
          // Additional from AppConfig
          depositMinWithSlip: config.depositInfo?.deposit_min_with_slip || 100,
          depositTurnoverBonusX:
            config.depositInfo?.deposit_turnover_bonus_x || 5,
          depositTurnoverMoneyX:
            config.depositInfo?.deposit_turnover_money_x || 1,
          depositTurnoverResetBelow:
            config.depositInfo?.deposit_turnover_reset_below || 5,
          withdrawLimit: Number(config.withdrawInfo?.withdraw_limit) || 60,
          withdrawMaxPerday: config.withdrawInfo?.withdraw_max_perday || 5,
          autoButton: config.withdrawInfo?.autoButton || [
            100, 200, 300, 500, 1000,
          ],
        });
        setAgentSettings({
          AGENT_USERNAME: config.agentInfo?.AGENT_USERNAME || "",
          AGENT_KEY: config.agentInfo?.AGENT_KEY || "",
          AGENT_WEB: config.agentInfo?.AGENT_WEB || "",
          AGENT_PREFIX: config.agentInfo?.AGENT_PREFIX || "",
          AGENT_TEMPLATE: config.agentInfo?.AGENT_TEMPLATE || "",
        });
        setCommissionSettings({
          m1_percent: config.commission?.m1_percent ?? 5,
          m2_percent: config.commission?.m2_percent ?? 2,
          p1_percent: config.commission?.p1_percent ?? 10,
          p2_percent: config.commission?.p2_percent ?? 5,
          company_percent: config.commission?.company_percent ?? 0,
          count_from: config.commission?.count_from || "vl",
          return_to: config.commission?.return_to || "cash",
          withdraw_min: config.commission?.withdraw_min ?? 100,
          withdraw_available_from: config.commission?.withdraw_available_from || "06:00",
          member_game_group_ids: config.commission?.member_game_group_ids ?? [],
          partner_game_group_ids: config.commission?.partner_game_group_ids ?? [],
          message: config.commission?.message || "",
        });
        setSlipSettings({
          easyslip_enabled: config.slipInfo?.easyslip_enabled || false,
          easyslip_endpoint_url: config.slipInfo?.easyslip_endpoint_url || "",
          easyslip_key: config.slipInfo?.easyslip_key || "",
          slip2go_enabled: config.slipInfo?.slip2go_enabled || false,
          slip2go_endpoint_url: config.slipInfo?.slip2go_endpoint_url || "",
          slip2go_key: config.slipInfo?.slip2go_key || "",
        });
        setWebbankSettings({
          truewallet_endpoint_url:
            config.webbankInfo?.truewallet_endpoint_url || "",
          truewallet_key: config.webbankInfo?.truewallet_key || "",
          truewallet_first_verify:
            config.webbankInfo?.truewallet_first_verify || false,
        });
        // ✅ Feature Flags (V2)
        setFeatureFlagsSettings({
          useWithdrawV2: config.featureFlags?.useWithdrawV2 || false,
          useDepositV2: config.featureFlags?.useDepositV2 || false,
          enableAuditLog: config.featureFlags?.enableAuditLog ?? true,
          enableAutoApprove: config.featureFlags?.enableAutoApprove || false,
        });
        // ✅ Security Settings (จาก securityConfig)
        setSecuritySettings({
          enable2FA:
            config.securityConfig?.enable2FA ??
            config.featureFlags?.enable2FA ??
            false,
          require2FAForAdmin:
            config.securityConfig?.require2FAForAdmin ??
            config.featureFlags?.require2FAForAdmin ??
            false,
          sessionTimeout: config.securityConfig?.sessionTimeout ?? 60,
          maxLoginAttempts: config.securityConfig?.maxLoginAttempts ?? 5,
          lockoutDuration: config.securityConfig?.lockoutDuration ?? 30,
          ipWhitelist: config.securityConfig?.ipWhitelist ?? false,
          allowedIPs: config.securityConfig?.allowedIPs ?? [],
          forcePasswordChange: config.securityConfig?.forcePasswordChange ?? 90,
          minPasswordLength: config.securityConfig?.minPasswordLength ?? 8,
          requireSpecialChar: config.securityConfig?.requireSpecialChar ?? true,
          enableCaptcha: config.securityConfig?.enableCaptcha ?? false,
          enableSSL: config.securityConfig?.enableSSL ?? false,
        });
        // ✅ AI Agent Config
        setAiAgentSettings({
          enabled: config.aiAgentConfig?.enabled ?? true,
          endpoint:
            config.aiAgentConfig?.endpoint ||
            "https://api.openai.com/v1/chat/completions",
          apiKey: config.aiAgentConfig?.apiKey || "",
          model: config.aiAgentConfig?.model || "gpt-4",
          temperature: config.aiAgentConfig?.temperature ?? 0.7,
          maxTokens: config.aiAgentConfig?.maxTokens ?? 1000,
          systemPrompt:
            config.aiAgentConfig?.systemPrompt ||
            "คุณเป็น AI Assistant ที่ช่วยเหลือผู้ดูแลระบบจัดการเว็บพนันออนไลน์ คุณสามารถช่วยตอบคำถามเกี่ยวกับยอดฝาก-ถอน, สมาชิก, รายการรอดำเนินการ, และอื่นๆ",
          showQuickActions: config.aiAgentConfig?.showQuickActions ?? true,
          autoExpand: config.aiAgentConfig?.autoExpand ?? false,
        });
        // ✅ Telegram Config
        setNotificationSettings((prev) => ({
          ...prev,
          telegramEnabled: config.telegramConfig?.enabled ?? false,
          telegramBotToken: config.telegramConfig?.botToken || "",
          telegramChatId: config.telegramConfig?.defaultChatId || "",
          telegramChannels: config.telegramConfig?.channels || [],
        }));
      }
    } catch (error) {
      console.error("[Settings] Load error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // เปิด confirm dialog ก่อนบันทึก
  const handleSaveClick = () => {
    setShowConfirmSave(true);
  };

  // บันทึกจริงหลังยืนยัน
  const handleConfirmSave = async () => {
    try {
      setIsSaving(true);
      setSaveMessage(null);

      const service = getAppConfigService();

      // ⚠️ เฉพาะ fields ที่ UI นี้จัดการเท่านั้น
      // Service จะ merge กับข้อมูลเดิม ไม่ลบข้อมูลที่ไม่ได้ส่ง
      const configToSave: Partial<AppConfig> = {
        // Agent & TrueWallet
        agentInfo: {
          AGENT_USERNAME: agentSettings.AGENT_USERNAME,
          AGENT_KEY: agentSettings.AGENT_KEY,
          AGENT_WEB: agentSettings.AGENT_WEB,
          AGENT_PREFIX: agentSettings.AGENT_PREFIX,
          AGENT_TEMPLATE: agentSettings.AGENT_TEMPLATE,
        },
        webbankInfo: {
          truewallet_endpoint_url: webbankSettings.truewallet_endpoint_url,
          truewallet_key: webbankSettings.truewallet_key,
          truewallet_first_verify: webbankSettings.truewallet_first_verify,
        },

        // Finance - Deposit
        depositInfo: {
          deposit_min: financeSettings.minDeposit,
          deposit_max: financeSettings.maxDeposit,
          deposit_min_with_slip: financeSettings.depositMinWithSlip ?? 100,
          deposit_turnover_bonus_x: financeSettings.depositTurnoverBonusX ?? 5,
          deposit_turnover_money_x: financeSettings.depositTurnoverMoneyX ?? 1,
          deposit_turnover_reset_below:
            financeSettings.depositTurnoverResetBelow ?? 5,
        },

        // Finance - Withdraw
        withdrawInfo: {
          withdraw_min: financeSettings.minWithdraw,
          withdraw_max: financeSettings.maxWithdraw,
          withdraw_after_deposit_time: financeSettings.withdrawProcessTime,
          withdraw_after_deposit_percent: financeSettings.withdrawFeePercent,
          withdraw_limit: financeSettings.withdrawLimit ?? 60,
          withdraw_max_perday: financeSettings.withdrawMaxPerday ?? 5,
          withdraw_max_perday_amount: financeSettings.maxWithdrawPerDay,
          autoButton: financeSettings.autoButton ?? [100, 200, 300, 500, 1000],
        },

        // Commission V2
        commission: {
          m1_percent: commissionSettings.m1_percent,
          m2_percent: commissionSettings.m2_percent,
          p1_percent: commissionSettings.p1_percent,
          p2_percent: commissionSettings.p2_percent,
          company_percent: commissionSettings.company_percent,
          count_from: commissionSettings.count_from,
          return_to: commissionSettings.return_to,
          withdraw_min: commissionSettings.withdraw_min,
          withdraw_available_from: commissionSettings.withdraw_available_from,
          member_game_group_ids: commissionSettings.member_game_group_ids,
          partner_game_group_ids: commissionSettings.partner_game_group_ids,
          message: commissionSettings.message,
        },

        // Slip Verification
        slipInfo: {
          easyslip_enabled: slipSettings.easyslip_enabled,
          easyslip_endpoint_url: slipSettings.easyslip_endpoint_url,
          easyslip_key: slipSettings.easyslip_key,
          slip2go_enabled: slipSettings.slip2go_enabled,
          slip2go_endpoint_url: slipSettings.slip2go_endpoint_url,
          slip2go_key: slipSettings.slip2go_key,
        },

        // Website Info - only update what we show
        websiteInfo: {
          siteName: generalSettings.siteName,
          publicUrl: generalSettings.siteUrl,
          // ไม่ส่ง publicFrontUrl, publicFrontApi, publicUrlSignup เพราะ UI ไม่แสดง
          // จะใช้ค่าเดิมจากการ merge
          publicFrontUrl: appConfig?.websiteInfo?.publicFrontUrl || "",
          publicFrontApi: appConfig?.websiteInfo?.publicFrontApi || "",
          publicUrlSignup: appConfig?.websiteInfo?.publicUrlSignup || "",
        },

        // ✅ Feature Flags (V2)
        featureFlags: {
          useWithdrawV2: featureFlagsSettings.useWithdrawV2,
          useDepositV2: featureFlagsSettings.useDepositV2,
          enableAuditLog: featureFlagsSettings.enableAuditLog,
          enableAutoApprove: featureFlagsSettings.enableAutoApprove,
          // ✅ 2FA Settings (เก็บใน featureFlags เพื่อ backward compatibility)
          enable2FA: securitySettings.enable2FA,
          require2FAForAdmin: securitySettings.require2FAForAdmin,
        },
        // ✅ Security Config
        securityConfig: {
          enable2FA: securitySettings.enable2FA,
          require2FAForAdmin: securitySettings.require2FAForAdmin,
          sessionTimeout: securitySettings.sessionTimeout,
          maxLoginAttempts: securitySettings.maxLoginAttempts,
          lockoutDuration: securitySettings.lockoutDuration,
          ipWhitelist: securitySettings.ipWhitelist,
          allowedIPs: securitySettings.allowedIPs,
          forcePasswordChange: securitySettings.forcePasswordChange,
          minPasswordLength: securitySettings.minPasswordLength,
          requireSpecialChar: securitySettings.requireSpecialChar,
          enableCaptcha: securitySettings.enableCaptcha,
          enableSSL: securitySettings.enableSSL,
        },
        aiAgentConfig: {
          enabled: aiAgentSettings.enabled,
          endpoint: aiAgentSettings.endpoint,
          apiKey: aiAgentSettings.apiKey,
          model: aiAgentSettings.model,
          temperature: aiAgentSettings.temperature,
          maxTokens: aiAgentSettings.maxTokens,
          systemPrompt: aiAgentSettings.systemPrompt,
          showQuickActions: aiAgentSettings.showQuickActions,
          autoExpand: aiAgentSettings.autoExpand,
        },
        // ✅ Telegram Config (Multi-Channel Support)
        telegramConfig: {
          enabled: notificationSettings.telegramEnabled,
          botToken: notificationSettings.telegramBotToken,
          // Use first channel's chatId as default, or legacy value
          defaultChatId: notificationSettings.telegramChannels[0]?.chatId || notificationSettings.telegramChatId,
          channels: notificationSettings.telegramChannels,
        },
      };

      console.log("💾 [Settings] Saving config:", configToSave);

      await service.saveAppConfig(configToSave);

      setShowConfirmSave(false);
      setSaveMessage({
        type: "success",
        message: "บันทึกการตั้งค่าเรียบร้อยแล้ว",
      });

      // Refresh cache
      const cacheResult = await service.refreshCache();
      console.log("🔄 [Settings] Cache refresh:", cacheResult);

      // Reload data to show updated values
      await loadAppConfig();
    } catch (error: any) {
      console.error("❌ [Settings] Save error:", error);
      setSaveMessage({
        type: "error",
        message: error.message || "เกิดข้อผิดพลาดในการบันทึก",
      });
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  // General Settings
  const [generalSettings, setGeneralSettings] = useState({
    siteName: "WINNER99",
    siteUrl: "https://winner99.com",
    siteLogo: "/logo.png",
    siteDescription: "เกมส์ออนไลน์อันดับ 1",
    contactPhone: "02-xxx-xxxx",
    contactEmail: "support@winner99.com",
    contactLine: "@winner99",
    timezone: "Asia/Bangkok",
    currency: "THB",
    language: "th",
  });

  // Agent Settings (from AppConfig)
  const [agentSettings, setAgentSettings] = useState({
    AGENT_USERNAME: "",
    AGENT_KEY: "",
    AGENT_WEB: "",
    AGENT_PREFIX: "",
    AGENT_TEMPLATE: "",
  });

  // Commission Settings V2 (from AppConfig)
  const [commissionSettings, setCommissionSettings] = useState({
    m1_percent: 5,
    m2_percent: 2,
    p1_percent: 10,
    p2_percent: 5,
    company_percent: 0,
    count_from: "vl",
    return_to: "cash",
    withdraw_min: 100,
    withdraw_available_from: "06:00",
    member_game_group_ids: [] as string[],
    partner_game_group_ids: [] as string[],
    message: "",
  });

  // Slip Settings (from AppConfig)
  const [slipSettings, setSlipSettings] = useState({
    easyslip_enabled: false,
    easyslip_endpoint_url: "",
    easyslip_key: "",
    slip2go_enabled: false,
    slip2go_endpoint_url: "",
    slip2go_key: "",
  });

  // Webbank Settings (from AppConfig)
  const [webbankSettings, setWebbankSettings] = useState({
    truewallet_endpoint_url: "",
    truewallet_key: "",
    truewallet_first_verify: false,
  });

  // Finance Settings (includes all AppConfig fields)
  const [financeSettings, setFinanceSettings] = useState<any>({
    // Deposit
    minDeposit: 100,
    maxDeposit: 500000,
    depositMinWithSlip: 100,
    depositTurnoverBonusX: 5,
    depositTurnoverMoneyX: 1,
    depositTurnoverResetBelow: 5,
    depositBonus: true,
    firstDepositBonus: 100,
    // Withdraw
    minWithdraw: 300,
    maxWithdraw: 200000,
    maxWithdrawPerDay: 1000000,
    withdrawFee: 0,
    withdrawFeePercent: 0,
    withdrawProcessTime: 5,
    withdrawLimit: 60,
    withdrawMaxPerday: 5,
    autoButton: [100, 200, 300, 500, 1000],
    autoApproveWithdraw: true,
    autoApproveLimit: 10000,
  });

  // Member Settings
  const [memberSettings, setMemberSettings] = useState({
    allowRegistration: true,
    requirePhone: true,
    requireEmail: false,
    requireBankAccount: true,
    minAge: 18,
    referralBonus: 50,
    enableReferral: true,
    autoApproveRegistration: true,
    welcomeBonus: true,
    welcomeBonusAmount: 50,
    vipLevels: ["Bronze", "Silver", "Gold", "Platinum", "VIP"],
  });

  // Promotion Settings
  const [promotionSettings, setPromotionSettings] = useState({
    defaultTurnover: 5,
    maxBonusPercent: 100,
    bonusExpireDays: 7,
    allowMultipleBonus: false,
    requireTurnoverBeforeWithdraw: true,
    cashbackEnabled: true,
    cashbackPercent: 5,
    cashbackDay: "Monday",
    lossbackEnabled: true,
    lossbackPercent: 10,
  });

  // Telegram Channel Type
  interface TelegramChannel {
    id: string;
    name: string;
    chatId: string;
    enabled: boolean;
    description?: string;
  }

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    telegramEnabled: false,
    telegramBotToken: "",
    telegramChatId: "", // Default Chat ID (for backward compatibility)
    telegramChannels: [] as TelegramChannel[],
    smsEnabled: false, // Disabled by default - Coming Soon
    smsProvider: "ThaiBulkSMS",
    smsApiKey: "xxxxxxxxxxxxx",
    emailEnabled: true,
    emailSmtp: "smtp.gmail.com",
    emailPort: 587,
    notifyDeposit: true,
    notifyWithdraw: true,
    notifyNewMember: true,
    notifyLargeTransaction: true,
    largeTransactionAmount: 50000,
  });

  // Security Settings
  const [securitySettings, setSecuritySettings] = useState({
    enable2FA: false,
    require2FAForAdmin: false,
    sessionTimeout: 60,
    maxLoginAttempts: 5,
    lockoutDuration: 30,
    ipWhitelist: false,
    allowedIPs: [] as string[],
    forcePasswordChange: 90,
    minPasswordLength: 8,
    requireSpecialChar: true,
    enableCaptcha: false, // ✅ ปิด Captcha
    enableSSL: false, // ✅ ปิด SSL
  });

  // System Settings
  const [systemSettings, setSystemSettings] = useState({
    maintenanceMode: false,
    maintenanceMessage: "ระบบกำลังปรับปรุง กรุณารอสักครู่",
    maintenanceEndTime: "",
    enableCache: true,
    cacheExpiry: 3600,
    debugMode: false,
    logLevel: "info",
    backupEnabled: true,
    backupSchedule: "daily",
    backupRetention: 30,
  });

  // ✅ Feature Flags Settings (V2)
  const [featureFlagsSettings, setFeatureFlagsSettings] = useState({
    useWithdrawV2: false,
    useDepositV2: false,
    enableAuditLog: true,
    enableAutoApprove: false,
  });

  const [aiAgentSettings, setAiAgentSettings] = useState({
    enabled: true,
    endpoint: "https://api.openai.com/v1/chat/completions",
    apiKey: "",
    model: "gpt-4",
    temperature: 0.7,
    maxTokens: 1000,
    systemPrompt:
      "คุณเป็น AI Assistant ที่ช่วยเหลือผู้ดูแลระบบจัดการเว็บพนันออนไลน์ คุณสามารถช่วยตอบคำถามเกี่ยวกับยอดฝาก-ถอน, สมาชิก, รายการรอดำเนินการ, และอื่นๆ",
    showQuickActions: true,
    autoExpand: false,
  });

  // ✅ Telegram Test State
  const [isTestingTelegram, setIsTestingTelegram] = useState(false);
  const [testingChannelId, setTestingChannelId] = useState<string | null>(null);
  const [telegramTestResult, setTelegramTestResult] = useState<{
    success: boolean;
    message: string;
    channelId?: string;
  } | null>(null);

  // ✅ Test Telegram Handler (for default/specific channel)
  const handleTestTelegram = async (channelId?: string, chatIdOverride?: string) => {
    try {
      setIsTestingTelegram(true);
      setTestingChannelId(channelId || null);
      setTelegramTestResult(null);

      const config = getCurrentParseConfig();
      const sessionToken = localStorage.getItem("sessionToken");

      // Determine which chatId to use
      const chatId = chatIdOverride || notificationSettings.telegramChatId;

      const response = await fetch(
        `${config.serverURL}/functions/testTelegramNotification`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Parse-Application-Id": config.appId,
            ...(sessionToken
              ? { "X-Parse-Session-Token": sessionToken }
              : { "X-Parse-Master-Key": config.masterKey }),
          },
          body: JSON.stringify({
            botToken: notificationSettings.telegramBotToken,
            chatId: chatId,
          }),
        }
      );

      const result = await response.json();

      if (result.result?.success) {
        setTelegramTestResult({
          success: true,
          message: "ส่งข้อความทดสอบสำเร็จ!",
          channelId: channelId,
        });
      } else {
        setTelegramTestResult({
          success: false,
          message: result.result?.error || result.error || "ไม่สามารถส่งข้อความได้",
          channelId: channelId,
        });
      }
    } catch (error: any) {
      setTelegramTestResult({
        success: false,
        message: error.message || "เกิดข้อผิดพลาด",
        channelId: channelId,
      });
    } finally {
      setIsTestingTelegram(false);
      setTestingChannelId(null);
    }
  };

  // ✅ Telegram Channel Management
  const handleAddChannel = () => {
    const newChannel = {
      id: `channel_${Date.now()}`,
      name: "New Channel",
      chatId: "",
      enabled: true,
      description: "",
    };
    setNotificationSettings({
      ...notificationSettings,
      telegramChannels: [...notificationSettings.telegramChannels, newChannel],
    });
  };

  const handleUpdateChannel = (index: number, updates: Partial<TelegramChannel>) => {
    const channels = [...notificationSettings.telegramChannels];
    channels[index] = { ...channels[index], ...updates };
    setNotificationSettings({
      ...notificationSettings,
      telegramChannels: channels,
    });
  };

  const handleRemoveChannel = (index: number) => {
    const channels = notificationSettings.telegramChannels.filter((_, i) => i !== index);
    setNotificationSettings({
      ...notificationSettings,
      telegramChannels: channels,
    });
  };

  const renderContent = () => {
    switch (activeCategory) {
      case "general":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">ข้อมูลเว็บไซต์</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>ชื่อเว็บไซต์</Label>
                  <Input
                    value={generalSettings.siteName}
                    onChange={(e) =>
                      setGeneralSettings({
                        ...generalSettings,
                        siteName: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL เว็บไซต์</Label>
                  <Input
                    value={generalSettings.siteUrl}
                    onChange={(e) =>
                      setGeneralSettings({
                        ...generalSettings,
                        siteUrl: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>คำอธิบายเว็บไซต์</Label>
                  <Input
                    value={generalSettings.siteDescription}
                    onChange={(e) =>
                      setGeneralSettings({
                        ...generalSettings,
                        siteDescription: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold mb-4">ข้อมูลติดต่อ</h3>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Phone className="h-4 w-4" /> โทรศัพท์
                  </Label>
                  <Input
                    value={generalSettings.contactPhone}
                    onChange={(e) =>
                      setGeneralSettings({
                        ...generalSettings,
                        contactPhone: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mail className="h-4 w-4" /> อีเมล
                  </Label>
                  <Input
                    value={generalSettings.contactEmail}
                    onChange={(e) =>
                      setGeneralSettings({
                        ...generalSettings,
                        contactEmail: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" /> LINE ID
                  </Label>
                  <Input
                    value={generalSettings.contactLine}
                    onChange={(e) =>
                      setGeneralSettings({
                        ...generalSettings,
                        contactLine: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold mb-4">ตั้งค่าระบบ</h3>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Globe className="h-4 w-4" /> Timezone
                  </Label>
                  <Input value={generalSettings.timezone} disabled />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" /> สกุลเงิน
                  </Label>
                  <Input value={generalSettings.currency} disabled />
                </div>
                <div className="space-y-2">
                  <Label>ภาษาเริ่มต้น</Label>
                  <Input
                    value={
                      generalSettings.language === "th" ? "ไทย" : "English"
                    }
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case "agent":
        return (
          <div className="space-y-6">
            {/* Info Banner */}
            <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <Key className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900 dark:text-blue-100">
                    การเชื่อมต่อ Agent เกม
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    ข้อมูลสำหรับเชื่อมต่อกับ Provider เกมต่างๆ
                  </p>
                </div>
              </div>
            </div>

            {/* Agent Credentials */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">ข้อมูล Agent</CardTitle>
                <CardDescription>
                  รหัสและข้อมูลสำหรับเชื่อมต่อกับระบบเกม
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label>Username</Label>
                  <Input
                    value={agentSettings.AGENT_USERNAME}
                    onChange={(e) =>
                      setAgentSettings({
                        ...agentSettings,
                        AGENT_USERNAME: e.target.value,
                      })
                    }
                    placeholder="ชื่อผู้ใช้"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Secret Key</Label>
                  <Input
                    type="password"
                    value={agentSettings.AGENT_KEY}
                    onChange={(e) =>
                      setAgentSettings({
                        ...agentSettings,
                        AGENT_KEY: e.target.value,
                      })
                    }
                    placeholder="รหัสลับ"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website URL</Label>
                  <Input
                    value={agentSettings.AGENT_WEB}
                    onChange={(e) =>
                      setAgentSettings({
                        ...agentSettings,
                        AGENT_WEB: e.target.value,
                      })
                    }
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prefix</Label>
                  <Input
                    value={agentSettings.AGENT_PREFIX}
                    onChange={(e) =>
                      setAgentSettings({
                        ...agentSettings,
                        AGENT_PREFIX: e.target.value,
                      })
                    }
                    placeholder="ABC"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Template</Label>
                  <Input
                    value={agentSettings.AGENT_TEMPLATE}
                    onChange={(e) =>
                      setAgentSettings({
                        ...agentSettings,
                        AGENT_TEMPLATE: e.target.value,
                      })
                    }
                    placeholder="default"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "finance":
        return (
          <div className="space-y-6">
            {/* Currency Settings */}
            <CurrencySettings />

            <Separator />

            {/* ===== การฝากเงิน ===== */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                  การฝากเงิน
                </CardTitle>
                <CardDescription>
                  ตั้งค่าขีดจำกัดและเงื่อนไขการฝาก
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label>ฝากขั้นต่ำ</Label>
                    <Input
                      type="number"
                      value={financeSettings.minDeposit}
                      onChange={(e) =>
                        setFinanceSettings({
                          ...financeSettings,
                          minDeposit: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>ฝากสูงสุด</Label>
                    <Input
                      type="number"
                      value={financeSettings.maxDeposit}
                      onChange={(e) =>
                        setFinanceSettings({
                          ...financeSettings,
                          maxDeposit: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>ฝากขั้นต่ำ (พร้อมสลิป)</Label>
                    <Input
                      type="number"
                      value={financeSettings.depositMinWithSlip}
                      onChange={(e) =>
                        setFinanceSettings({
                          ...financeSettings,
                          depositMinWithSlip: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-muted/30">
                  <p className="text-sm font-medium mb-3">เทิร์นโอเวอร์</p>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label className="text-xs">เทิร์นโบนัส (เท่า)</Label>
                      <Input
                        type="number"
                        value={financeSettings.depositTurnoverBonusX}
                        onChange={(e) =>
                          setFinanceSettings({
                            ...financeSettings,
                            depositTurnoverBonusX: Number(e.target.value),
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">เทิร์นเงิน (เท่า)</Label>
                      <Input
                        type="number"
                        value={financeSettings.depositTurnoverMoneyX}
                        onChange={(e) =>
                          setFinanceSettings({
                            ...financeSettings,
                            depositTurnoverMoneyX: Number(e.target.value),
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">
                        รีเซ็ตเทิร์นเมื่อต่ำกว่า
                      </Label>
                      <Input
                        type="number"
                        value={financeSettings.depositTurnoverResetBelow}
                        onChange={(e) =>
                          setFinanceSettings({
                            ...financeSettings,
                            depositTurnoverResetBelow: Number(e.target.value),
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ===== การถอนเงิน ===== */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full" />
                  การถอนเงิน
                </CardTitle>
                <CardDescription>
                  ตั้งค่าขีดจำกัดและเงื่อนไขการถอน
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label>ถอนขั้นต่ำ</Label>
                    <Input
                      type="number"
                      value={financeSettings.minWithdraw}
                      onChange={(e) =>
                        setFinanceSettings({
                          ...financeSettings,
                          minWithdraw: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>ถอนสูงสุดต่อครั้ง</Label>
                    <Input
                      type="number"
                      value={financeSettings.maxWithdraw}
                      onChange={(e) =>
                        setFinanceSettings({
                          ...financeSettings,
                          maxWithdraw: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>ถอนสูงสุดต่อวัน (บาท)</Label>
                    <Input
                      type="number"
                      value={financeSettings.maxWithdrawPerDay}
                      onChange={(e) =>
                        setFinanceSettings({
                          ...financeSettings,
                          maxWithdrawPerDay: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label>จำนวนครั้งสูงสุด/วัน</Label>
                    <Input
                      type="number"
                      value={financeSettings.withdrawMaxPerday}
                      onChange={(e) =>
                        setFinanceSettings({
                          ...financeSettings,
                          withdrawMaxPerday: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>เวลารอหลังฝาก (นาที)</Label>
                    <Input
                      type="number"
                      value={financeSettings.withdrawProcessTime}
                      onChange={(e) =>
                        setFinanceSettings({
                          ...financeSettings,
                          withdrawProcessTime: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>% หลังฝากก่อนถอน</Label>
                    <Input
                      type="number"
                      value={financeSettings.withdrawFeePercent}
                      onChange={(e) =>
                        setFinanceSettings({
                          ...financeSettings,
                          withdrawFeePercent: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-muted/30">
                  <div className="space-y-2">
                    <Label className="text-xs">ปุ่มถอนด่วน (คั่นด้วย ,)</Label>
                    <Input
                      value={
                        financeSettings.autoButton?.join(",") ||
                        "100,200,300,500,1000"
                      }
                      onChange={(e) => {
                        const values = e.target.value
                          .split(",")
                          .map((v) => Number(v.trim()))
                          .filter((v) => !isNaN(v));
                        setFinanceSettings({
                          ...financeSettings,
                          autoButton: values,
                        });
                      }}
                      placeholder="100,200,300,500,1000"
                    />
                    <p className="text-xs text-muted-foreground">
                      ปุ่มจำนวนเงินสำหรับถอนด่วน
                    </p>
                  </div>
                </div>

                {/* Auto Approve Switch */}
                <Separator />
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-background border">
                    <div className="flex-1">
                      <Label className="text-base font-medium">
                        Auto Approve
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        {featureFlagsSettings.enableAutoApprove
                          ? "เปิดใช้งาน: รายการถอนที่ต่ำกว่า Withdraw Limit จะถูกอนุมัติอัตโนมัติ (รวมถึงรายการที่มีโปรโมชัน)"
                          : "ปิดใช้งาน: รายการถอนทั้งหมดจะต้องรอการอนุมัติจาก Admin"}
                      </p>
                    </div>
                    <Switch
                      checked={featureFlagsSettings.enableAutoApprove}
                      onCheckedChange={(checked) =>
                        setFeatureFlagsSettings({
                          ...featureFlagsSettings,
                          enableAutoApprove: checked,
                        })
                      }
                    />
                  </div>

                  {/* Withdraw Limit - แสดงเฉพาะเมื่อเปิด Auto Approve */}
                  {featureFlagsSettings.enableAutoApprove && (
                    <div className="p-4 rounded-lg bg-muted/30 border">
                      <div className="space-y-2">
                        <Label>Withdraw Limit (บาท)</Label>
                        <Input
                          type="number"
                          value={financeSettings.withdrawLimit}
                          onChange={(e) =>
                            setFinanceSettings({
                              ...financeSettings,
                              withdrawLimit: Number(e.target.value),
                            })
                          }
                          placeholder="10000"
                        />
                        <p className="text-xs text-muted-foreground">
                          รายการถอนที่มากกว่าหรือเท่ากับค่านี้จะต้องรอการอนุมัติจาก
                          Admin
                          <br />
                          รายการถอนที่ต่ำกว่าค่านี้จะถูกอนุมัติอัตโนมัติ
                          (รวมถึงรายการที่มีโปรโมชัน max_withdraw)
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* ===== TrueWallet ===== */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <div className="w-2 h-2 bg-orange-500 rounded-full" />
                  TrueWallet
                </CardTitle>
                <CardDescription>
                  ตั้งค่าการเชื่อมต่อ TrueWallet API
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800">
                  <div>
                    <Label>บังคับยืนยันตัวตนครั้งแรก</Label>
                    <p className="text-xs text-muted-foreground">
                      สมาชิกต้องยืนยัน TrueWallet ก่อนใช้งาน
                    </p>
                  </div>
                  <Switch
                    checked={webbankSettings.truewallet_first_verify}
                    onCheckedChange={(checked) =>
                      setWebbankSettings({
                        ...webbankSettings,
                        truewallet_first_verify: checked,
                      })
                    }
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Endpoint URL</Label>
                    <Input
                      value={webbankSettings.truewallet_endpoint_url}
                      onChange={(e) =>
                        setWebbankSettings({
                          ...webbankSettings,
                          truewallet_endpoint_url: e.target.value,
                        })
                      }
                      placeholder="https://api.truewallet.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>API Key</Label>
                    <Input
                      type="password"
                      value={webbankSettings.truewallet_key}
                      onChange={(e) =>
                        setWebbankSettings({
                          ...webbankSettings,
                          truewallet_key: e.target.value,
                        })
                      }
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "commission": {
        const toggleMemberGame = (value: string) => {
          const ids = commissionSettings.member_game_group_ids || [];
          const next = ids.includes(value)
            ? ids.filter((id) => id !== value)
            : [...ids, value];
          setCommissionSettings({ ...commissionSettings, member_game_group_ids: next });
        };
        const togglePartnerGame = (value: string) => {
          const ids = commissionSettings.partner_game_group_ids || [];
          const next = ids.includes(value)
            ? ids.filter((id) => id !== value)
            : [...ids, value];
          setCommissionSettings({ ...commissionSettings, partner_game_group_ids: next });
        };
        const memberGameIds = commissionSettings.member_game_group_ids || [];
        const partnerGameIds = commissionSettings.partner_game_group_ids || [];
        const allSelected = (ids: string[]) => ids.length === GAME_GROUPS.length;
        const toggleAllMember = () => {
          setCommissionSettings({
            ...commissionSettings,
            member_game_group_ids: allSelected(memberGameIds) ? [] : GAME_GROUPS.map((g) => g.value),
          });
        };
        const toggleAllPartner = () => {
          setCommissionSettings({
            ...commissionSettings,
            partner_game_group_ids: allSelected(partnerGameIds) ? [] : GAME_GROUPS.map((g) => g.value),
          });
        };

        return (
          <div className="space-y-6">
            {/* Info Banner - Commission V2 */}
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <Percent className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-amber-900 dark:text-amber-100">
                      ระบบแนะนำเพื่อน (Affiliate)
                    </p>
                    <Badge variant="secondary" className="text-xs">
                      Commission V2
                    </Badge>
                  </div>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    ตั้งค่าอัตราคอมมิชชั่นสำหรับสมาชิกและพาร์ทเนอร์ แยกของใครของมัน
                  </p>
                </div>
              </div>
            </div>

            {/* คอมมิชชั่น สมาชิก */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">คอมมิชชั่น สมาชิก</CardTitle>
                <CardDescription>
                  อัตราที่สมาชิกได้รับเมื่อชวนเพื่อนสมัคร (0–100%)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>ชั้น 1 (M1) %</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={commissionSettings.m1_percent}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setCommissionSettings({
                          ...commissionSettings,
                          m1_percent: Number.isNaN(v) ? 0 : Math.min(100, Math.max(0, v)),
                        });
                      }}
                    />
                    <p className="text-xs text-muted-foreground">จากคนที่ชวนโดยตรง</p>
                  </div>
                  <div className="space-y-2">
                    <Label>ชั้น 2 (M2) %</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={commissionSettings.m2_percent}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setCommissionSettings({
                          ...commissionSettings,
                          m2_percent: Number.isNaN(v) ? 0 : Math.min(100, Math.max(0, v)),
                        });
                      }}
                    />
                    <p className="text-xs text-muted-foreground">จากคนที่ถูกชวนต่อ</p>
                  </div>
                </div>

                {/* เงื่อนไขการคำนวณ - สมาชิก */}
                <Separator />
                <div>
                  <Label className="text-sm font-medium mb-3 block">เงื่อนไขการคำนวณ (สมาชิก)</Label>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label>คำนวณจาก</Label>
                      <Select
                        value={commissionSettings.count_from || "vl"}
                        onValueChange={(value) =>
                          setCommissionSettings({ ...commissionSettings, count_from: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="เลือกฐานคำนวณ" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="wl">WL (Win Lose)</SelectItem>
                          <SelectItem value="vl">VL (เทิน / Turnover)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>คืนเข้า</Label>
                      <Select
                        value={commissionSettings.return_to || "cash"}
                        onValueChange={(value) =>
                          setCommissionSettings({ ...commissionSettings, return_to: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="เลือกจุดคืน" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">เงินสด</SelectItem>
                          <SelectItem value="bonus">โบนัส</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>ถอนขั้นต่ำ (บาท)</Label>
                      <Input
                        type="number"
                        min={0}
                        value={commissionSettings.withdraw_min}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setCommissionSettings({
                            ...commissionSettings,
                            withdraw_min: Number.isNaN(v) ? 0 : Math.max(0, v),
                          });
                        }}
                      />
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2 mt-4">
                    <div className="space-y-2">
                      <Label>ถอนได้ตั้งแต่เวลา</Label>
                      <Select
                        value={commissionSettings.withdraw_available_from || "06:00"}
                        onValueChange={(value) =>
                          setCommissionSettings({ ...commissionSettings, withdraw_available_from: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="เลือกเวลา" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="04:00">04:00 น.</SelectItem>
                          <SelectItem value="05:00">05:00 น.</SelectItem>
                          <SelectItem value="06:00">06:00 น.</SelectItem>
                          <SelectItem value="07:00">07:00 น.</SelectItem>
                          <SelectItem value="08:00">08:00 น.</SelectItem>
                          <SelectItem value="09:00">09:00 น.</SelectItem>
                          <SelectItem value="10:00">10:00 น.</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        รอให้ระบบคำนวณเสร็จก่อน (cron รัน 03:00-05:00 น.)
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    <Label className="flex items-center gap-2">
                      <Gamepad2 className="h-4 w-4 text-muted-foreground" />
                      กลุ่มเกมที่ใช้คำนวณ (VL)
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      เลือกกลุ่มเกมที่จะนำมาคำนวณ VL (ไม่เลือก = ทุกกลุ่ม)
                    </p>
                    <div className="flex items-center gap-2 mb-2">
                      <Checkbox
                        id="member-all-games"
                        checked={allSelected(memberGameIds)}
                        onCheckedChange={toggleAllMember}
                      />
                      <label htmlFor="member-all-games" className="text-sm cursor-pointer">
                        เลือกทุกกลุ่ม
                      </label>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                      {GAME_GROUPS.map((game) => (
                        <label
                          key={game.value}
                          className={cn(
                            "flex items-center gap-2 p-2.5 border rounded-lg cursor-pointer transition-all text-sm",
                            "hover:bg-muted/50 hover:border-primary",
                            memberGameIds.includes(game.value) && "border-primary bg-primary/5"
                          )}
                        >
                          <Checkbox
                            checked={memberGameIds.includes(game.value)}
                            onCheckedChange={() => toggleMemberGame(game.value)}
                          />
                          <span className="truncate">{game.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    <Label>ข้อความแนะนำเพื่อน</Label>
                    <Input
                      value={commissionSettings.message}
                      onChange={(e) =>
                        setCommissionSettings({ ...commissionSettings, message: e.target.value })
                      }
                      placeholder="ข้อความที่จะแสดงในหน้าแนะนำเพื่อน"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* คอมมิชชั่น พาร์ทเนอร์ */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">คอมมิชชั่น พาร์ทเนอร์</CardTitle>
                <CardDescription>
                  อัตราพิเศษสำหรับพาร์ทเนอร์/ตัวแทน (0–100%) ไม่มีคืนเข้า/ถอนขั้นต่ำ
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label>ชั้น 1 (P1) %</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={commissionSettings.p1_percent}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setCommissionSettings({
                          ...commissionSettings,
                          p1_percent: Number.isNaN(v) ? 0 : Math.min(100, Math.max(0, v)),
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>ชั้น 2 (P2) %</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={commissionSettings.p2_percent}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setCommissionSettings({
                          ...commissionSettings,
                          p2_percent: Number.isNaN(v) ? 0 : Math.min(100, Math.max(0, v)),
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>เปอร์เซ็น Company %</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={commissionSettings.company_percent}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setCommissionSettings({
                          ...commissionSettings,
                          company_percent: Number.isNaN(v) ? 0 : Math.min(100, Math.max(0, v)),
                        });
                      }}
                    />
                    <p className="text-xs text-muted-foreground">ส่วนแบ่ง Company</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Gamepad2 className="h-4 w-4 text-muted-foreground" />
                    กลุ่มเกมที่ใช้คำนวณ
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    เลือกกลุ่มเกมที่จะนำมาคำนวณ (ไม่เลือก = ทุกกลุ่ม)
                  </p>
                  <div className="flex items-center gap-2 mb-2">
                    <Checkbox
                      id="partner-all-games"
                      checked={allSelected(partnerGameIds)}
                      onCheckedChange={toggleAllPartner}
                    />
                    <label htmlFor="partner-all-games" className="text-sm cursor-pointer">
                      เลือกทุกกลุ่ม
                    </label>
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                    {GAME_GROUPS.map((game) => (
                      <label
                        key={game.value}
                        className={cn(
                          "flex items-center gap-2 p-2.5 border rounded-lg cursor-pointer transition-all text-sm",
                          "hover:bg-muted/50 hover:border-primary",
                          partnerGameIds.includes(game.value) && "border-primary bg-primary/5"
                        )}
                      >
                        <Checkbox
                          checked={partnerGameIds.includes(game.value)}
                          onCheckedChange={() => togglePartnerGame(game.value)}
                        />
                        <span className="truncate">{game.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      }

      case "ai":
        return (
          <div className="space-y-6">
            {/* AI Agent Enable/Disable */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bot className="h-5 w-5 text-purple-600" />
                  เปิด/ปิด AI Assistant
                </CardTitle>
                <CardDescription>
                  ควบคุมการแสดงผล AI Assistant ที่มุมขวาล่างของหน้าจอ
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label className="text-base font-medium">
                      เปิดใช้งาน AI Assistant
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      เมื่อเปิดใช้งาน จะแสดงปุ่ม AI Assistant ที่มุมขวาล่าง
                    </p>
                  </div>
                  <Switch
                    checked={aiAgentSettings.enabled}
                    onCheckedChange={(checked) =>
                      setAiAgentSettings({
                        ...aiAgentSettings,
                        enabled: checked,
                      })
                    }
                  />
                </div>
              </CardContent>
            </Card>

            {/* API Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings2 className="h-5 w-5 text-blue-600" />
                  การตั้งค่า API
                </CardTitle>
                <CardDescription>
                  ตั้งค่า endpoint และ API key สำหรับเชื่อมต่อกับ AI service
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ai-endpoint">API Endpoint</Label>
                  <Input
                    id="ai-endpoint"
                    value={aiAgentSettings.endpoint}
                    onChange={(e) =>
                      setAiAgentSettings({
                        ...aiAgentSettings,
                        endpoint: e.target.value,
                      })
                    }
                    placeholder="https://api.openai.com/v1/chat/completions"
                    disabled={!aiAgentSettings.enabled}
                  />
                  <p className="text-xs text-muted-foreground">
                    URL ของ API endpoint สำหรับ AI service
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ai-api-key">API Key</Label>
                  <div className="flex gap-2">
                    <Input
                      id="ai-api-key"
                      type="password"
                      value={aiAgentSettings.apiKey}
                      onChange={(e) =>
                        setAiAgentSettings({
                          ...aiAgentSettings,
                          apiKey: e.target.value,
                        })
                      }
                      placeholder="sk-..."
                      disabled={!aiAgentSettings.enabled}
                      className="font-mono"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        navigator.clipboard.writeText(aiAgentSettings.apiKey);
                        setSaveMessage({
                          type: "success",
                          message: "คัดลอก API Key แล้ว",
                        });
                      }}
                      disabled={!aiAgentSettings.apiKey}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    API key สำหรับ authentication (จะถูกเข้ารหัสเมื่อบันทึก)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Model Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-green-600" />
                  การตั้งค่า Model
                </CardTitle>
                <CardDescription>
                  กำหนด model และพารามิเตอร์สำหรับ AI
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ai-model">Model</Label>
                    <Input
                      id="ai-model"
                      value={aiAgentSettings.model}
                      onChange={(e) =>
                        setAiAgentSettings({
                          ...aiAgentSettings,
                          model: e.target.value,
                        })
                      }
                      placeholder="gpt-4"
                      disabled={!aiAgentSettings.enabled}
                    />
                    <p className="text-xs text-muted-foreground">
                      ชื่อ model ที่จะใช้ (เช่น gpt-4, gpt-3.5-turbo)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ai-temperature">Temperature</Label>
                    <Input
                      id="ai-temperature"
                      type="number"
                      min="0"
                      max="2"
                      step="0.1"
                      value={aiAgentSettings.temperature}
                      onChange={(e) =>
                        setAiAgentSettings({
                          ...aiAgentSettings,
                          temperature: parseFloat(e.target.value) || 0.7,
                        })
                      }
                      disabled={!aiAgentSettings.enabled}
                    />
                    <p className="text-xs text-muted-foreground">
                      ความสร้างสรรค์ (0-2) - สูง = สร้างสรรค์มากขึ้น
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ai-max-tokens">Max Tokens</Label>
                    <Input
                      id="ai-max-tokens"
                      type="number"
                      min="100"
                      max="4000"
                      step="100"
                      value={aiAgentSettings.maxTokens}
                      onChange={(e) =>
                        setAiAgentSettings({
                          ...aiAgentSettings,
                          maxTokens: parseInt(e.target.value) || 1000,
                        })
                      }
                      disabled={!aiAgentSettings.enabled}
                    />
                    <p className="text-xs text-muted-foreground">
                      จำนวน tokens สูงสุดต่อ response
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* System Prompt */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-orange-600" />
                  System Prompt
                </CardTitle>
                <CardDescription>
                  กำหนดบทบาทและพฤติกรรมของ AI Assistant
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ai-system-prompt">Prompt</Label>
                  <textarea
                    id="ai-system-prompt"
                    className="w-full min-h-[120px] px-3 py-2 text-sm border rounded-md resize-y"
                    value={aiAgentSettings.systemPrompt}
                    onChange={(e) =>
                      setAiAgentSettings({
                        ...aiAgentSettings,
                        systemPrompt: e.target.value,
                      })
                    }
                    placeholder="คุณเป็น AI Assistant ที่ช่วยเหลือ..."
                    disabled={!aiAgentSettings.enabled}
                  />
                  <p className="text-xs text-muted-foreground">
                    คำแนะนำสำหรับ AI เกี่ยวกับบทบาทและพฤติกรรม
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* UI Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-600" />
                  การตั้งค่า UI
                </CardTitle>
                <CardDescription>
                  ควบคุมการแสดงผลและพฤติกรรมของ UI
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label className="text-base font-medium">
                      แสดง Quick Actions
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      แสดงปุ่มคำสั่งด่วน (สรุปยอด, สมาชิกใหม่, ฯลฯ)
                    </p>
                  </div>
                  <Switch
                    checked={aiAgentSettings.showQuickActions}
                    onCheckedChange={(checked) =>
                      setAiAgentSettings({
                        ...aiAgentSettings,
                        showQuickActions: checked,
                      })
                    }
                    disabled={!aiAgentSettings.enabled}
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label className="text-base font-medium">
                      ขยายหน้าต่างอัตโนมัติ
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      ขยายหน้าต่าง AI Assistant อัตโนมัติเมื่อเปิด
                    </p>
                  </div>
                  <Switch
                    checked={aiAgentSettings.autoExpand}
                    onCheckedChange={(checked) =>
                      setAiAgentSettings({
                        ...aiAgentSettings,
                        autoExpand: checked,
                      })
                    }
                    disabled={!aiAgentSettings.enabled}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "slip":
        return (
          <div className="space-y-6">
            {/* EasySlip */}
            <div
              className={cn(
                "p-4 rounded-lg border",
                slipSettings.easyslip_enabled
                  ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800"
                  : "bg-muted/30"
              )}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Receipt className="h-5 w-5 text-emerald-600" />
                  <div>
                    <p className="font-semibold">EasySlip</p>
                    <p className="text-xs text-muted-foreground">
                      ระบบตรวจสอบสลิปอัตโนมัติ
                    </p>
                  </div>
                </div>
                <Switch
                  checked={slipSettings.easyslip_enabled}
                  onCheckedChange={(checked) =>
                    setSlipSettings({
                      ...slipSettings,
                      easyslip_enabled: checked,
                      // ปิด slip2go ถ้าเปิด easyslip
                      slip2go_enabled: checked
                        ? false
                        : slipSettings.slip2go_enabled,
                    })
                  }
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Endpoint URL</Label>
                  <Input
                    value={slipSettings.easyslip_endpoint_url}
                    onChange={(e) =>
                      setSlipSettings({
                        ...slipSettings,
                        easyslip_endpoint_url: e.target.value,
                      })
                    }
                    placeholder="https://api.easyslip.com"
                    disabled={!slipSettings.easyslip_enabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    value={slipSettings.easyslip_key}
                    onChange={(e) =>
                      setSlipSettings({
                        ...slipSettings,
                        easyslip_key: e.target.value,
                      })
                    }
                    placeholder="API Key"
                    disabled={!slipSettings.easyslip_enabled}
                  />
                </div>
              </div>
            </div>

            {/* Slip2Go */}
            <div
              className={cn(
                "p-4 rounded-lg border",
                slipSettings.slip2go_enabled
                  ? "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800"
                  : "bg-muted/30"
              )}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Receipt className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="font-semibold">Slip2Go</p>
                    <p className="text-xs text-muted-foreground">
                      ระบบตรวจสอบสลิปทางเลือก
                    </p>
                  </div>
                </div>
                <Switch
                  checked={slipSettings.slip2go_enabled}
                  onCheckedChange={(checked) =>
                    setSlipSettings({
                      ...slipSettings,
                      slip2go_enabled: checked,
                      // ปิด easyslip ถ้าเปิด slip2go
                      easyslip_enabled: checked
                        ? false
                        : slipSettings.easyslip_enabled,
                    })
                  }
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Endpoint URL</Label>
                  <Input
                    value={slipSettings.slip2go_endpoint_url}
                    onChange={(e) =>
                      setSlipSettings({
                        ...slipSettings,
                        slip2go_endpoint_url: e.target.value,
                      })
                    }
                    placeholder="https://api.slip2go.com"
                    disabled={!slipSettings.slip2go_enabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    value={slipSettings.slip2go_key}
                    onChange={(e) =>
                      setSlipSettings({
                        ...slipSettings,
                        slip2go_key: e.target.value,
                      })
                    }
                    placeholder="API Key"
                    disabled={!slipSettings.slip2go_enabled}
                  />
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  <p className="font-medium mb-1">หมายเหตุ</p>
                  <ul className="list-disc list-inside space-y-1 text-amber-700 dark:text-amber-300">
                    <li>สามารถเปิดใช้งานได้เพียง 1 ระบบในเวลาเดียวกัน</li>
                    <li>การเปลี่ยนระบบจะมีผลทันทีหลังบันทึก</li>
                    <li>ตรวจสอบ API Key ก่อนเปิดใช้งาน</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case "members":
        return (
          <div className="space-y-6">
            {/* Mock Data Banner */}
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900 dark:text-amber-100">
                    ข้อมูลตัวอย่าง (Mock)
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    ส่วนนี้ยังไม่ได้เชื่อม API - การเปลี่ยนแปลงจะไม่บันทึก
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">การสมัครสมาชิก</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label>เปิดรับสมัครสมาชิก</Label>
                    <p className="text-xs text-muted-foreground">
                      อนุญาตให้ผู้ใช้สมัครใหม่
                    </p>
                  </div>
                  <Switch
                    checked={memberSettings.allowRegistration}
                    onCheckedChange={(checked) =>
                      setMemberSettings({
                        ...memberSettings,
                        allowRegistration: checked,
                      })
                    }
                  />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label>อนุมัติอัตโนมัติ</Label>
                    <p className="text-xs text-muted-foreground">
                      สมาชิกใหม่ใช้งานได้ทันที
                    </p>
                  </div>
                  <Switch
                    checked={memberSettings.autoApproveRegistration}
                    onCheckedChange={(checked) =>
                      setMemberSettings({
                        ...memberSettings,
                        autoApproveRegistration: checked,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold mb-4">ข้อมูลที่จำเป็น</h3>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label>เบอร์โทรศัพท์</Label>
                    <p className="text-xs text-muted-foreground">บังคับกรอก</p>
                  </div>
                  <Switch
                    checked={memberSettings.requirePhone}
                    onCheckedChange={(checked) =>
                      setMemberSettings({
                        ...memberSettings,
                        requirePhone: checked,
                      })
                    }
                  />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label>อีเมล</Label>
                    <p className="text-xs text-muted-foreground">บังคับกรอก</p>
                  </div>
                  <Switch
                    checked={memberSettings.requireEmail}
                    onCheckedChange={(checked) =>
                      setMemberSettings({
                        ...memberSettings,
                        requireEmail: checked,
                      })
                    }
                  />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label>บัญชีธนาคาร</Label>
                    <p className="text-xs text-muted-foreground">บังคับกรอก</p>
                  </div>
                  <Switch
                    checked={memberSettings.requireBankAccount}
                    onCheckedChange={(checked) =>
                      setMemberSettings({
                        ...memberSettings,
                        requireBankAccount: checked,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold mb-4">ระบบแนะนำเพื่อน</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label>เปิดระบบแนะนำเพื่อน</Label>
                    <p className="text-xs text-muted-foreground">
                      ให้สมาชิกชวนเพื่อน
                    </p>
                  </div>
                  <Switch
                    checked={memberSettings.enableReferral}
                    onCheckedChange={(checked) =>
                      setMemberSettings({
                        ...memberSettings,
                        enableReferral: checked,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>โบนัสแนะนำเพื่อน (บาท)</Label>
                  <Input
                    type="number"
                    value={memberSettings.referralBonus}
                    onChange={(e) =>
                      setMemberSettings({
                        ...memberSettings,
                        referralBonus: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold mb-4">โบนัสต้อนรับ</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label>เปิดโบนัสต้อนรับ</Label>
                    <p className="text-xs text-muted-foreground">
                      ให้โบนัสสมาชิกใหม่
                    </p>
                  </div>
                  <Switch
                    checked={memberSettings.welcomeBonus}
                    onCheckedChange={(checked) =>
                      setMemberSettings({
                        ...memberSettings,
                        welcomeBonus: checked,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>จำนวนโบนัส (บาท)</Label>
                  <Input
                    type="number"
                    value={memberSettings.welcomeBonusAmount}
                    onChange={(e) =>
                      setMemberSettings({
                        ...memberSettings,
                        welcomeBonusAmount: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case "promotions":
        return (
          <div className="space-y-6">
            {/* Mock Data Banner */}
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900 dark:text-amber-100">
                    ข้อมูลตัวอย่าง (Mock)
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    ส่วนนี้ยังไม่ได้เชื่อม API - การเปลี่ยนแปลงจะไม่บันทึก
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">ตั้งค่าโบนัส</h3>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>เทิร์นโอเวอร์เริ่มต้น (เท่า)</Label>
                  <Input
                    type="number"
                    value={promotionSettings.defaultTurnover}
                    onChange={(e) =>
                      setPromotionSettings({
                        ...promotionSettings,
                        defaultTurnover: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>โบนัสสูงสุด (%)</Label>
                  <Input
                    type="number"
                    value={promotionSettings.maxBonusPercent}
                    onChange={(e) =>
                      setPromotionSettings({
                        ...promotionSettings,
                        maxBonusPercent: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>หมดอายุใน (วัน)</Label>
                  <Input
                    type="number"
                    value={promotionSettings.bonusExpireDays}
                    onChange={(e) =>
                      setPromotionSettings({
                        ...promotionSettings,
                        bonusExpireDays: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 mt-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label>รับหลายโบนัสพร้อมกัน</Label>
                    <p className="text-xs text-muted-foreground">
                      อนุญาตให้รับโบนัสซ้อน
                    </p>
                  </div>
                  <Switch
                    checked={promotionSettings.allowMultipleBonus}
                    onCheckedChange={(checked) =>
                      setPromotionSettings({
                        ...promotionSettings,
                        allowMultipleBonus: checked,
                      })
                    }
                  />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label>ต้องทำเทิร์นก่อนถอน</Label>
                    <p className="text-xs text-muted-foreground">
                      บังคับทำเทิร์นโอเวอร์
                    </p>
                  </div>
                  <Switch
                    checked={promotionSettings.requireTurnoverBeforeWithdraw}
                    onCheckedChange={(checked) =>
                      setPromotionSettings({
                        ...promotionSettings,
                        requireTurnoverBeforeWithdraw: checked,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold mb-4">
                Cashback & Lossback
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 rounded-lg bg-muted/30 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>เปิด Cashback</Label>
                      <p className="text-xs text-muted-foreground">
                        คืนยอดเล่น
                      </p>
                    </div>
                    <Switch
                      checked={promotionSettings.cashbackEnabled}
                      onCheckedChange={(checked) =>
                        setPromotionSettings({
                          ...promotionSettings,
                          cashbackEnabled: checked,
                        })
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">เปอร์เซ็นต์</Label>
                      <Input
                        type="number"
                        value={promotionSettings.cashbackPercent}
                        onChange={(e) =>
                          setPromotionSettings({
                            ...promotionSettings,
                            cashbackPercent: Number(e.target.value),
                          })
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">วันที่จ่าย</Label>
                      <Input
                        value={promotionSettings.cashbackDay}
                        onChange={(e) =>
                          setPromotionSettings({
                            ...promotionSettings,
                            cashbackDay: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-muted/30 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>เปิด Lossback</Label>
                      <p className="text-xs text-muted-foreground">
                        คืนยอดเสีย
                      </p>
                    </div>
                    <Switch
                      checked={promotionSettings.lossbackEnabled}
                      onCheckedChange={(checked) =>
                        setPromotionSettings({
                          ...promotionSettings,
                          lossbackEnabled: checked,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">เปอร์เซ็นต์</Label>
                    <Input
                      type="number"
                      value={promotionSettings.lossbackPercent}
                      onChange={(e) =>
                        setPromotionSettings({
                          ...promotionSettings,
                          lossbackPercent: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case "notifications":
        return (
          <div className="space-y-6">
            {/* API Connected Banner - Telegram */}
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-green-900 dark:text-green-100">
                    Telegram - เชื่อมต่อ API แล้ว
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    การตั้งค่า Telegram จะถูกบันทึกในฐานข้อมูล (ส่วนอื่นยังเป็น Mock)
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Bot className="w-5 h-5" />
                Telegram Notifications
              </h3>
              <div className="p-4 rounded-lg bg-muted/30 space-y-4">
                {/* Header: Enable + Bot Token */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={notificationSettings.telegramEnabled}
                      onCheckedChange={(checked) =>
                        setNotificationSettings({
                          ...notificationSettings,
                          telegramEnabled: checked,
                        })
                      }
                    />
                    <div>
                      <Label>เปิดใช้ Telegram</Label>
                      <p className="text-xs text-muted-foreground">
                        แจ้งเตือนผ่าน Telegram Bot
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddChannel}
                    disabled={!notificationSettings.telegramEnabled}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    เพิ่ม Channel
                  </Button>
                </div>

                {/* Bot Token - Compact */}
                <div className="flex items-center gap-3">
                  <Label className="text-sm whitespace-nowrap w-20">Bot Token</Label>
                  <Input
                    type="password"
                    placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
                    value={notificationSettings.telegramBotToken}
                    onChange={(e) =>
                      setNotificationSettings({
                        ...notificationSettings,
                        telegramBotToken: e.target.value,
                      })
                    }
                    className="flex-1"
                  />
                </div>

                {/* Channel List */}
                {notificationSettings.telegramChannels.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
                    <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-40" />
                    <p className="text-sm font-medium">ยังไม่มี Channel</p>
                    <p className="text-xs">กดปุ่ม "เพิ่ม Channel" เพื่อสร้าง Notification Channel</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {notificationSettings.telegramChannels.map((channel, index) => (
                      <div
                        key={channel.id}
                        className={cn(
                          "p-3 border rounded-lg transition-colors",
                          channel.enabled 
                            ? "bg-background border-border" 
                            : "bg-muted/30 border-muted opacity-60"
                        )}
                      >
                        {/* Row 1: Toggle + Name + Actions */}
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={channel.enabled}
                            onCheckedChange={(checked) =>
                              handleUpdateChannel(index, { enabled: checked })
                            }
                          />
                          <div className="flex-1 grid grid-cols-3 gap-2">
                            <Input
                              placeholder="admin"
                              value={channel.id}
                              onChange={(e) =>
                                handleUpdateChannel(index, { id: e.target.value })
                              }
                              className="h-8 text-sm font-mono"
                            />
                            <Input
                              placeholder="Channel Name"
                              value={channel.name}
                              onChange={(e) =>
                                handleUpdateChannel(index, { name: e.target.value })
                              }
                              className="h-8 text-sm"
                            />
                            <div className="flex gap-1">
                              <Input
                                placeholder="-100xxxxxxxxxx"
                                value={channel.chatId}
                                onChange={(e) =>
                                  handleUpdateChannel(index, { chatId: e.target.value })
                                }
                                className="h-8 text-sm font-mono flex-1"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-2"
                                onClick={() => handleTestTelegram(channel.id, channel.chatId)}
                                disabled={
                                  !notificationSettings.telegramEnabled ||
                                  !notificationSettings.telegramBotToken ||
                                  !channel.chatId ||
                                  (isTestingTelegram && testingChannelId === channel.id)
                                }
                              >
                                {isTestingTelegram && testingChannelId === channel.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Send className="h-3 w-3" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleRemoveChannel(index)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Row 2: Description (collapsible feel) */}
                        {channel.description && (
                          <p className="text-xs text-muted-foreground mt-2 ml-12">
                            {channel.description}
                          </p>
                        )}

                        {/* Test Result */}
                        {telegramTestResult && telegramTestResult.channelId === channel.id && (
                          <p
                            className={cn(
                              "text-xs mt-2 ml-12",
                              telegramTestResult.success ? "text-green-600" : "text-red-600"
                            )}
                          >
                            {telegramTestResult.success ? "✓" : "✗"} {telegramTestResult.message}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Channel Labels Legend */}
                {notificationSettings.telegramChannels.length > 0 && (
                  <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t">
                    <span className="flex items-center gap-1">
                      <span className="w-16">Channel ID</span>
                      <span className="text-muted-foreground/50">|</span>
                      <span className="w-24">ชื่อ</span>
                      <span className="text-muted-foreground/50">|</span>
                      <span>Chat ID</span>
                    </span>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* SMS Section - Disabled/Coming Soon */}
            <div className="opacity-50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  SMS
                </h3>
                <Badge variant="secondary" className="text-xs">Coming Soon</Badge>
              </div>
              <div className="p-4 rounded-lg bg-muted/30">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-muted-foreground">เปิดใช้ SMS</Label>
                    <p className="text-xs text-muted-foreground">
                      ส่ง SMS แจ้งเตือน (ยังไม่พร้อมใช้งาน)
                    </p>
                  </div>
                  <Switch
                    checked={false}
                    disabled
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Notification Events - Enhanced */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5" />
                เหตุการณ์ที่แจ้งเตือน
              </h3>

              {/* Financial Events */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  💰 การเงิน
                </h4>
                <div className="rounded-lg border overflow-hidden">
                  {/* Deposit */}
                  <div className="grid grid-cols-12 gap-2 p-3 items-center border-b hover:bg-muted/20">
                    <div className="col-span-1 text-center">💵</div>
                    <div className="col-span-6">
                      <span className="font-medium text-sm">รายการฝากเงิน</span>
                      <p className="text-xs text-muted-foreground">แจ้งเตือนทุกครั้งที่มีรายการฝาก</p>
                    </div>
                    <div className="col-span-3">
                      <Badge variant="outline" className="text-xs">admin</Badge>
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <Switch
                        checked={notificationSettings.notifyDeposit}
                        onCheckedChange={(checked) =>
                          setNotificationSettings({
                            ...notificationSettings,
                            notifyDeposit: checked,
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* Withdraw */}
                  <div className="grid grid-cols-12 gap-2 p-3 items-center border-b hover:bg-muted/20">
                    <div className="col-span-1 text-center">💸</div>
                    <div className="col-span-6">
                      <span className="font-medium text-sm">รายการถอนเงิน</span>
                      <p className="text-xs text-muted-foreground">แจ้งเตือนทุกครั้งที่มีรายการถอน</p>
                    </div>
                    <div className="col-span-3">
                      <Badge variant="outline" className="text-xs">admin</Badge>
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <Switch
                        checked={notificationSettings.notifyWithdraw}
                        onCheckedChange={(checked) =>
                          setNotificationSettings({
                            ...notificationSettings,
                            notifyWithdraw: checked,
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* Large Transaction */}
                  <div className="grid grid-cols-12 gap-2 p-3 items-center border-b hover:bg-muted/20">
                    <div className="col-span-1 text-center">⚠️</div>
                    <div className="col-span-6">
                      <span className="font-medium text-sm">รายการยอดใหญ่</span>
                      <p className="text-xs text-muted-foreground">
                        ยอดมากกว่า ฿{notificationSettings.largeTransactionAmount.toLocaleString()}
                      </p>
                    </div>
                    <div className="col-span-3">
                      <Badge variant="outline" className="text-xs">admin</Badge>
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <Switch
                        checked={notificationSettings.notifyLargeTransaction}
                        onCheckedChange={(checked) =>
                          setNotificationSettings({
                            ...notificationSettings,
                            notifyLargeTransaction: checked,
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* Lossback */}
                  <div className="grid grid-cols-12 gap-2 p-3 items-center border-b hover:bg-muted/20">
                    <div className="col-span-1 text-center">🔄</div>
                    <div className="col-span-6">
                      <span className="font-medium text-sm">คืนยอดเสีย (Lossback)</span>
                      <p className="text-xs text-muted-foreground">แจ้งเตือนเมื่อมีการคืนยอดเสียให้สมาชิก</p>
                    </div>
                    <div className="col-span-3">
                      <Badge variant="outline" className="text-xs">admin</Badge>
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <Switch defaultChecked />
                    </div>
                  </div>

                  {/* Daily Summary */}
                  <div className="grid grid-cols-12 gap-2 p-3 items-center hover:bg-muted/20">
                    <div className="col-span-1 text-center">📊</div>
                    <div className="col-span-6">
                      <span className="font-medium text-sm">สรุปยอดประจำวัน</span>
                      <p className="text-xs text-muted-foreground">รายงานยอดฝาก-ถอน-กำไร ทุกวัน 00:00</p>
                    </div>
                    <div className="col-span-3">
                      <Badge variant="outline" className="text-xs">admin</Badge>
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <Switch defaultChecked />
                    </div>
                  </div>
                </div>
              </div>

              {/* Member Events */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  👤 สมาชิก
                </h4>
                <div className="rounded-lg border overflow-hidden">
                  {/* New Member */}
                  <div className="grid grid-cols-12 gap-2 p-3 items-center border-b hover:bg-muted/20">
                    <div className="col-span-1 text-center">🆕</div>
                    <div className="col-span-6">
                      <span className="font-medium text-sm">สมาชิกใหม่</span>
                      <p className="text-xs text-muted-foreground">แจ้งเตือนเมื่อมีสมาชิกสมัครใหม่</p>
                    </div>
                    <div className="col-span-3">
                      <Badge variant="outline" className="text-xs">admin</Badge>
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <Switch
                        checked={notificationSettings.notifyNewMember}
                        onCheckedChange={(checked) =>
                          setNotificationSettings({
                            ...notificationSettings,
                            notifyNewMember: checked,
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* Member Blocked */}
                  <div className="grid grid-cols-12 gap-2 p-3 items-center hover:bg-muted/20">
                    <div className="col-span-1 text-center">🚫</div>
                    <div className="col-span-6">
                      <span className="font-medium text-sm">สมาชิกถูกบล็อก</span>
                      <p className="text-xs text-muted-foreground">แจ้งเตือนเมื่อสมาชิกถูกระงับการใช้งาน</p>
                    </div>
                    <div className="col-span-3">
                      <Badge variant="outline" className="text-xs">admin</Badge>
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <Switch defaultChecked />
                    </div>
                  </div>
                </div>
              </div>

              {/* Security Events */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  🔐 ความปลอดภัย
                </h4>
                <div className="rounded-lg border overflow-hidden">
                  {/* Admin Login */}
                  <div className="grid grid-cols-12 gap-2 p-3 items-center border-b hover:bg-muted/20">
                    <div className="col-span-1 text-center">🔑</div>
                    <div className="col-span-6">
                      <span className="font-medium text-sm">Admin Login</span>
                      <p className="text-xs text-muted-foreground">แจ้งเตือนเมื่อ Admin เข้าสู่ระบบ</p>
                    </div>
                    <div className="col-span-3">
                      <Badge variant="outline" className="text-xs">admin</Badge>
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <Switch defaultChecked />
                    </div>
                  </div>

                  {/* Failed Login */}
                  <div className="grid grid-cols-12 gap-2 p-3 items-center hover:bg-muted/20">
                    <div className="col-span-1 text-center">🚨</div>
                    <div className="col-span-6">
                      <span className="font-medium text-sm">Login ล้มเหลวหลายครั้ง</span>
                      <p className="text-xs text-muted-foreground">แจ้งเตือนเมื่อมีการพยายาม login ผิดพลาด 5 ครั้ง</p>
                    </div>
                    <div className="col-span-3">
                      <Badge variant="outline" className="text-xs">admin</Badge>
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <Switch defaultChecked />
                    </div>
                  </div>
                </div>
              </div>

              {/* System Events */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  ⚙️ ระบบ
                </h4>
                <div className="rounded-lg border overflow-hidden">
                  {/* Game Sync */}
                  <div className="grid grid-cols-12 gap-2 p-3 items-center border-b hover:bg-muted/20">
                    <div className="col-span-1 text-center">🎮</div>
                    <div className="col-span-6">
                      <span className="font-medium text-sm">Game Sync</span>
                      <p className="text-xs text-muted-foreground">รายงานการ Sync เกมจาก API</p>
                    </div>
                    <div className="col-span-3">
                      <Badge variant="outline" className="text-xs">admin</Badge>
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <Switch defaultChecked />
                    </div>
                  </div>

                  {/* Cron Job Failed */}
                  <div className="grid grid-cols-12 gap-2 p-3 items-center border-b hover:bg-muted/20">
                    <div className="col-span-1 text-center">⏰</div>
                    <div className="col-span-6">
                      <span className="font-medium text-sm">Cron Job Failed</span>
                      <p className="text-xs text-muted-foreground">แจ้งเตือนเมื่อ Cron Job ทำงานผิดพลาด</p>
                    </div>
                    <div className="col-span-3">
                      <Badge variant="outline" className="text-xs">admin</Badge>
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <Switch defaultChecked />
                    </div>
                  </div>

                  {/* System Error */}
                  <div className="grid grid-cols-12 gap-2 p-3 items-center border-b hover:bg-muted/20">
                    <div className="col-span-1 text-center">❌</div>
                    <div className="col-span-6">
                      <span className="font-medium text-sm">System Error</span>
                      <p className="text-xs text-muted-foreground">แจ้งเตือนเมื่อระบบเกิดข้อผิดพลาดร้ายแรง</p>
                    </div>
                    <div className="col-span-3">
                      <Badge variant="outline" className="text-xs">admin</Badge>
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <Switch defaultChecked />
                    </div>
                  </div>

                  {/* API Error */}
                  <div className="grid grid-cols-12 gap-2 p-3 items-center hover:bg-muted/20">
                    <div className="col-span-1 text-center">🔌</div>
                    <div className="col-span-6">
                      <span className="font-medium text-sm">API Error</span>
                      <p className="text-xs text-muted-foreground">แจ้งเตือนเมื่อเชื่อมต่อ API ภายนอกล้มเหลว</p>
                    </div>
                    <div className="col-span-3">
                      <Badge variant="outline" className="text-xs">admin</Badge>
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <Switch defaultChecked />
                    </div>
                  </div>
                </div>
              </div>

              {/* Note */}
              <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1">
                <CheckCircle className="h-3 w-3 text-green-500" />
                การแจ้งเตือนทั้งหมดจะส่งไปยัง Channel "admin" โดยอัตโนมัติ
              </p>
            </div>
          </div>
        );

      case "security":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">การยืนยันตัวตน</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label>เปิดใช้ 2FA</Label>
                    <p className="text-xs text-muted-foreground">
                      ยืนยันตัวตน 2 ขั้นตอน
                    </p>
                  </div>
                  <Switch
                    checked={securitySettings.enable2FA}
                    onCheckedChange={(checked) =>
                      setSecuritySettings({
                        ...securitySettings,
                        enable2FA: checked,
                      })
                    }
                  />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label>บังคับ 2FA สำหรับ Admin</Label>
                    <p className="text-xs text-muted-foreground">
                      แอดมินต้องใช้ 2FA
                    </p>
                  </div>
                  <Switch
                    checked={securitySettings.require2FAForAdmin}
                    onCheckedChange={(checked) =>
                      setSecuritySettings({
                        ...securitySettings,
                        require2FAForAdmin: checked,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold mb-4">Session & Login</h3>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Session Timeout (นาที)</Label>
                  <Input
                    type="number"
                    value={securitySettings.sessionTimeout}
                    onChange={(e) =>
                      setSecuritySettings({
                        ...securitySettings,
                        sessionTimeout: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>ล็อกอินผิดพลาดสูงสุด (ครั้ง)</Label>
                  <Input
                    type="number"
                    value={securitySettings.maxLoginAttempts}
                    onChange={(e) =>
                      setSecuritySettings({
                        ...securitySettings,
                        maxLoginAttempts: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>ระยะเวลาล็อก (นาที)</Label>
                  <Input
                    type="number"
                    value={securitySettings.lockoutDuration}
                    onChange={(e) =>
                      setSecuritySettings({
                        ...securitySettings,
                        lockoutDuration: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold mb-4">รหัสผ่าน</h3>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>ความยาวขั้นต่ำ</Label>
                  <Input
                    type="number"
                    value={securitySettings.minPasswordLength}
                    onChange={(e) =>
                      setSecuritySettings({
                        ...securitySettings,
                        minPasswordLength: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>บังคับเปลี่ยนทุก (วัน)</Label>
                  <Input
                    type="number"
                    value={securitySettings.forcePasswordChange}
                    onChange={(e) =>
                      setSecuritySettings({
                        ...securitySettings,
                        forcePasswordChange: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <Label>ต้องมีอักขระพิเศษ</Label>
                  <Switch
                    checked={securitySettings.requireSpecialChar}
                    onCheckedChange={(checked) =>
                      setSecuritySettings({
                        ...securitySettings,
                        requireSpecialChar: checked,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold mb-4">
                ความปลอดภัยเพิ่มเติม
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label>เปิดใช้ Captcha</Label>
                    <p className="text-xs text-muted-foreground">ป้องกัน Bot</p>
                  </div>
                  <Switch
                    checked={securitySettings.enableCaptcha}
                    onCheckedChange={(checked) =>
                      setSecuritySettings({
                        ...securitySettings,
                        enableCaptcha: checked,
                      })
                    }
                  />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label>บังคับใช้ SSL</Label>
                    <p className="text-xs text-muted-foreground">
                      HTTPS เท่านั้น
                    </p>
                  </div>
                  <Switch
                    checked={securitySettings.enableSSL}
                    onCheckedChange={(checked) =>
                      setSecuritySettings({
                        ...securitySettings,
                        enableSSL: checked,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case "system":
        return (
          <div className="space-y-6">
            {/* ✅ Feature Flags V2 Section - เชื่อม API แล้ว */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                Feature Flags (V2)
              </h3>
              <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  เปิด/ปิดฟีเจอร์ V2 - เมื่อเปิดจะใช้ logic
                  ใหม่ที่ออกแบบมาดีกว่า
                </p>

                {/* Withdraw V2 */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-background">
                  <div>
                    <Label className="text-base font-medium">Withdraw V2</Label>
                    <p className="text-xs text-muted-foreground">
                      ใช้ระบบถอนเงินแบบใหม่ (5 Status, 2-Layer)
                    </p>
                  </div>
                  <Switch
                    checked={featureFlagsSettings.useWithdrawV2}
                    onCheckedChange={(checked) =>
                      setFeatureFlagsSettings({
                        ...featureFlagsSettings,
                        useWithdrawV2: checked,
                      })
                    }
                  />
                </div>

                {/* Deposit V2 */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-background">
                  <div>
                    <Label className="text-base font-medium">Deposit V2</Label>
                    <p className="text-xs text-muted-foreground">
                      ใช้ระบบฝากเงินแบบใหม่ (5 Status, 2-Layer, Duplicate
                      Detection)
                    </p>
                  </div>
                  <Switch
                    checked={featureFlagsSettings.useDepositV2}
                    onCheckedChange={(checked) =>
                      setFeatureFlagsSettings({
                        ...featureFlagsSettings,
                        useDepositV2: checked,
                      })
                    }
                  />
                </div>

                {/* Enable Audit Log */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-background">
                  <div>
                    <Label className="text-base font-medium">Audit Log</Label>
                    <p className="text-xs text-muted-foreground">
                      บันทึกกิจกรรมทุกอย่างในระบบ
                    </p>
                  </div>
                  <Switch
                    checked={featureFlagsSettings.enableAuditLog}
                    onCheckedChange={(checked) =>
                      setFeatureFlagsSettings({
                        ...featureFlagsSettings,
                        enableAuditLog: checked,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Mock Data Banner */}
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900 dark:text-amber-100">
                    ข้อมูลตัวอย่าง (Mock) - ส่วนด้านล่าง
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    ส่วนนี้ยังไม่ได้เชื่อม API - การเปลี่ยนแปลงจะไม่บันทึก
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">โหมดปรับปรุง</h3>
              <div
                className={cn(
                  "p-4 rounded-lg space-y-4",
                  systemSettings.maintenanceMode
                    ? "bg-destructive/10 border border-destructive/30"
                    : "bg-muted/30"
                )}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <Label
                      className={
                        systemSettings.maintenanceMode ? "text-destructive" : ""
                      }
                    >
                      เปิดโหมดปรับปรุง
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      ปิดการเข้าถึงเว็บไซต์ชั่วคราว
                    </p>
                  </div>
                  <Switch
                    checked={systemSettings.maintenanceMode}
                    onCheckedChange={(checked) =>
                      setSystemSettings({
                        ...systemSettings,
                        maintenanceMode: checked,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>ข้อความแจ้งเตือน</Label>
                  <Input
                    value={systemSettings.maintenanceMessage}
                    onChange={(e) =>
                      setSystemSettings({
                        ...systemSettings,
                        maintenanceMessage: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold mb-4">Cache</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <Label>เปิดใช้ Cache</Label>
                    <p className="text-xs text-muted-foreground">
                      เพิ่มความเร็วระบบ
                    </p>
                  </div>
                  <Switch
                    checked={systemSettings.enableCache}
                    onCheckedChange={(checked) =>
                      setSystemSettings({
                        ...systemSettings,
                        enableCache: checked,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cache Expiry (วินาที)</Label>
                  <Input
                    type="number"
                    value={systemSettings.cacheExpiry}
                    onChange={(e) =>
                      setSystemSettings({
                        ...systemSettings,
                        cacheExpiry: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
              <Button variant="outline" className="mt-4 gap-2">
                <Zap className="h-4 w-4" />
                ล้าง Cache ทั้งหมด
              </Button>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold mb-4">สำรองข้อมูล</h3>
              <div className="p-4 rounded-lg bg-muted/30 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>เปิดใช้ Auto Backup</Label>
                    <p className="text-xs text-muted-foreground">
                      สำรองข้อมูลอัตโนมัติ
                    </p>
                  </div>
                  <Switch
                    checked={systemSettings.backupEnabled}
                    onCheckedChange={(checked) =>
                      setSystemSettings({
                        ...systemSettings,
                        backupEnabled: checked,
                      })
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ความถี่</Label>
                    <Input
                      value={systemSettings.backupSchedule}
                      onChange={(e) =>
                        setSystemSettings({
                          ...systemSettings,
                          backupSchedule: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>เก็บไว้ (วัน)</Label>
                    <Input
                      type="number"
                      value={systemSettings.backupRetention}
                      onChange={(e) =>
                        setSystemSettings({
                          ...systemSettings,
                          backupRetention: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>
                <Button variant="outline" className="gap-2">
                  <Database className="h-4 w-4" />
                  สำรองข้อมูลเดี๋ยวนี้
                </Button>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-semibold mb-4">Debug</h3>
              <div className="flex items-center justify-between p-4 rounded-lg bg-warning/10 border border-warning/30">
                <div>
                  <Label className="text-warning">Debug Mode</Label>
                  <p className="text-xs text-muted-foreground">
                    ⚠️ ไม่แนะนำสำหรับ Production
                  </p>
                </div>
                <Switch
                  checked={systemSettings.debugMode}
                  onCheckedChange={(checked) =>
                    setSystemSettings({ ...systemSettings, debugMode: checked })
                  }
                />
              </div>
            </div>
          </div>
        );
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">{t("settings.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t("settings.title")}</h1>
          <p className="text-muted-foreground">{t("settings.subtitle")}</p>
          {appConfig?.updatedAt && (
            <p className="text-xs text-muted-foreground mt-1">
              {t("settings.lastUpdate")}:{" "}
              {new Date(appConfig.updatedAt).toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/audit-log")}
            className="gap-2"
          >
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t("settings.editHistory")}
            </span>
          </Button>
          <Button
            onClick={handleSaveClick}
            disabled={isSaving}
            className="gap-2"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isSaving ? t("settings.saving") : t("settings.saveAll")}
          </Button>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div
          className={cn(
            "p-4 rounded-lg flex items-center gap-3",
            saveMessage.type === "success"
              ? "bg-emerald-50 border border-emerald-200 text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-200"
              : "bg-destructive/10 border border-destructive/30 text-destructive"
          )}
        >
          {saveMessage.type === "success" ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertTriangle className="h-5 w-5" />
          )}
          <p className="font-medium">{saveMessage.message}</p>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="lg:w-64 shrink-0">
          <Card>
            <CardContent className="p-2">
              <nav className="space-y-1">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors",
                      activeCategory === category.id
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted"
                    )}
                  >
                    <div
                      className={cn(
                        "p-1.5 rounded-md",
                        activeCategory === category.id
                          ? "bg-primary-foreground/20"
                          : "bg-muted"
                      )}
                    >
                      {category.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{category.label}</p>
                        {category.badge && (
                          <Badge
                            variant={
                              category.badge === "API" ? "default" : "secondary"
                            }
                            className={cn(
                              "text-[10px] px-1.5 py-0",
                              activeCategory === category.id
                                ? "bg-primary-foreground/20 text-primary-foreground"
                                : category.badge === "API"
                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                : category.badge === "DEV"
                                ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                : "bg-muted text-muted-foreground"
                            )}
                          >
                            {category.badge}
                          </Badge>
                        )}
                      </div>
                      <p
                        className={cn(
                          "text-xs truncate",
                          activeCategory === category.id
                            ? "text-primary-foreground/70"
                            : "text-muted-foreground"
                        )}
                      >
                        {category.description}
                      </p>
                    </div>
                  </button>
                ))}
              </nav>
            </CardContent>
          </Card>
        </div>

        {/* Content */}
        <Card className="flex-1">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                {categories.find((c) => c.id === activeCategory)?.icon}
              </div>
              <div>
                <CardTitle>
                  {categories.find((c) => c.id === activeCategory)?.label}
                </CardTitle>
                <CardDescription>
                  {categories.find((c) => c.id === activeCategory)?.description}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>{renderContent()}</CardContent>
        </Card>
      </div>

      {/* Confirm Save Dialog */}
      <SaveConfirmDialog
        open={showConfirmSave}
        onOpenChange={setShowConfirmSave}
        onConfirm={handleConfirmSave}
        isLoading={isSaving}
        itemName="การตั้งค่าระบบ"
        changes={[
          {
            field: `หมวด: ${
              categories.find((c) => c.id === activeCategory)?.label
            }`,
          },
        ]}
      />
    </div>
  );
}
